import React, {
    KeyboardEvent,
    MouseEvent,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Button, Input, Spin } from "antd";
import {
    ArrowRightOutlined,
    SearchOutlined,
    RightOutlined,
} from "@ant-design/icons";
import { WbSubject } from "../../workbench/types/WbTypes";
import WorkbenchNewEntityTypeTag from "./WorkbenchNewEntityTypeTag";
import WorkbenchNewHighlight from "./WorkbenchNewHighlight";

type Props = {
    error: string | null;
    isLoading: boolean;
    onSearchModeChange: (active: boolean) => void;
    onSelect: (subject: WbSubject) => void;
    query: string;
    setQuery: (value: string) => void;
    suggestions: WbSubject[];
    totalCount: number;
};

type SuggestionViewModel = {
    subject: WbSubject;
    title: string;
    identifier: string;
    party: string;
    period: string;
    rank: number;
};

const MAX_VISIBLE_SUGGESTIONS = 5;

const normalizeIdentifier = (value: string) =>
    value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const tokenize = (value: string) =>
    value.trim().toLowerCase().split(/\s+/).filter(Boolean);

const formatDateYear = (value?: string | null) => {
    if (!value) {
        return "";
    }

    const year = new Date(value).getFullYear();
    return Number.isNaN(year) ? "" : String(year);
};

const getSubjectParty = (subject: WbSubject) =>
    subject.cedent ??
    subject.context?.cedent ??
    subject.context?.claimant ??
    subject.context?.broker ??
    "";

const getSubjectPeriod = (subject: WbSubject) => {
    if (subject.kind === "Claim" && subject.context?.dateOfLoss) {
        return `Loss date ${subject.context.dateOfLoss}`;
    }

    if (subject.context?.uwYear) {
        return `UW Year ${subject.context.uwYear}`;
    }

    const periodYears = [
        formatDateYear(subject.periodFrom),
        formatDateYear(subject.periodTo),
    ].filter(Boolean);

    return Array.from(new Set(periodYears)).join("-");
};

const getSubjectTitle = (subject: WbSubject) => {
    const party = getSubjectParty(subject);
    const mainClass = subject.context?.mainClass?.trim();
    const year = subject.context?.uwYear;
    const base = party || subject.identifier;

    if (mainClass && year) {
        return `${base} ${mainClass} ${year}`;
    }

    if (mainClass) {
        return `${base} ${mainClass}`;
    }

    return base;
};

const getRank = (
    subject: WbSubject,
    query: string,
    title: string,
    party: string,
) => {
    const cleanQuery = query.trim();
    const normalizedQuery = normalizeIdentifier(cleanQuery);
    const normalizedIdentifier = normalizeIdentifier(subject.identifier);

    if (normalizedQuery && normalizedIdentifier === normalizedQuery) {
        return 0;
    }

    const lowerQuery = cleanQuery.toLowerCase();
    const tokens = tokenize(cleanQuery);
    const businessReference = [
        subject.identifier,
        title,
        party,
        subject.context?.broker ?? "",
        subject.context?.claimant ?? "",
        subject.context?.cedent ?? "",
    ]
        .join(" ")
        .toLowerCase();

    if (
        lowerQuery &&
        (subject.identifier.toLowerCase().startsWith(lowerQuery) ||
            title.toLowerCase().startsWith(lowerQuery) ||
            party.toLowerCase().startsWith(lowerQuery) ||
            tokens.every((token) => businessReference.includes(token)))
    ) {
        return 1;
    }

    return 2;
};

const buildSuggestionViewModels = (
    suggestions: WbSubject[],
    query: string,
): SuggestionViewModel[] => {
    const deduplicated = new Map<string, WbSubject>();

    suggestions.forEach((subject) => {
        const key = subject.id || `${subject.kind}:${subject.identifier}`;

        if (!deduplicated.has(key)) {
            deduplicated.set(key, subject);
        }
    });

    return Array.from(deduplicated.values())
        .map((subject) => {
            const title = getSubjectTitle(subject);
            const party = getSubjectParty(subject);

            return {
                subject,
                title,
                identifier: subject.identifier,
                party,
                period: getSubjectPeriod(subject),
                rank: getRank(subject, query, title, party),
            };
        })
        .sort(
            (left, right) =>
                left.rank - right.rank ||
                left.identifier.localeCompare(right.identifier),
        );
};

const WorkbenchNewSearch: React.FC<Props> = ({
    error,
    isLoading,
    onSearchModeChange,
    onSelect,
    query,
    setQuery,
    suggestions,
    totalCount,
}) => {
    const trimmedQuery = query.trim();
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [previousQuery, setPreviousQuery] = useState(query);

    const rankedSuggestions = useMemo(
        () => buildSuggestionViewModels(suggestions, query),
        [query, suggestions],
    );
    const visibleSuggestions = rankedSuggestions.slice(
        0,
        MAX_VISIBLE_SUGGESTIONS,
    );
    const visibleCount = Math.min(
        totalCount || rankedSuggestions.length,
        MAX_VISIBLE_SUGGESTIONS,
    );
    const totalResults = totalCount || rankedSuggestions.length;
    const showSearchInteraction = trimmedQuery.length >= 2;
    const showPanel = isOpen && showSearchInteraction;
    const exactIdentifierMatch =
        rankedSuggestions.find(
            (suggestion) =>
                normalizeIdentifier(suggestion.identifier) ===
                normalizeIdentifier(trimmedQuery),
        ) ?? null;
    const activeSuggestion =
        activeIndex >= 0 ? visibleSuggestions[activeIndex] : null;
    const activeOptionId = activeSuggestion
        ? `workbench-new-suggestion-${activeIndex}`
        : undefined;

    useEffect(() => {
        const queryChanged = previousQuery !== query;
        setPreviousQuery(query);
        setActiveIndex(-1);

        if (!showSearchInteraction) {
            setIsOpen(false);
            return;
        }

        if (queryChanged) {
            setIsOpen(true);
        }
    }, [previousQuery, query, showSearchInteraction]);

    useEffect(() => {
        onSearchModeChange(showPanel);
    }, [onSearchModeChange, showPanel]);

    const openSubject = (subject: WbSubject) => {
        setIsOpen(false);
        setActiveIndex(-1);
        onSelect(subject);
    };

    const openCurrentSubject = () => {
        if (activeSuggestion) {
            openSubject(activeSuggestion.subject);
            return;
        }

        if (exactIdentifierMatch) {
            openSubject(exactIdentifierMatch.subject);
            return;
        }

        if (rankedSuggestions.length === 1) {
            openSubject(rankedSuggestions[0].subject);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            setIsOpen(false);
            setActiveIndex(-1);
            return;
        }

        if (!showSearchInteraction) {
            return;
        }

        if (event.key === "ArrowDown" && visibleSuggestions.length > 0) {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((index) =>
                index < 0 ? 0 : (index + 1) % visibleSuggestions.length,
            );
            return;
        }

        if (event.key === "ArrowUp" && visibleSuggestions.length > 0) {
            event.preventDefault();
            setIsOpen(true);
            setActiveIndex((index) =>
                index <= 0 ? visibleSuggestions.length - 1 : index - 1,
            );
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            openCurrentSubject();
        }
    };

    const handlePointerSelect = (
        event: MouseEvent<HTMLElement>,
        suggestion: SuggestionViewModel,
    ) => {
        event.preventDefault();
        openSubject(suggestion.subject);
    };

    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const getFooterRightText = () => {
        if (
            activeSuggestion ||
            exactIdentifierMatch ||
            rankedSuggestions.length === 1
        ) {
            return "Press Enter to open";
        }

        return "Use ↑↓ to select";
    };

    const renderFooter = () => {
        if (isLoading || error || visibleSuggestions.length === 0) {
            return null;
        }

        return (
            <div className="autocomplete-footer workbench-new-search-footer">
                <span>
                    {totalResults > MAX_VISIBLE_SUGGESTIONS
                        ? `${totalResults} entities found · Refine your search`
                        : `${visibleCount} suggested ${
                              visibleCount === 1 ? "entity" : "entities"
                          }`}
                </span>
                <span>{getFooterRightText()}</span>
            </div>
        );
    };

    const renderPanelContent = () => {
        if (isLoading) {
            return (
                <div
                    className="autocomplete-state autocomplete-loading"
                    role="status"
                >
                    <Spin size="small" />
                    <span>Searching entities...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div
                    className="autocomplete-state autocomplete-error"
                    role="alert"
                >
                    <strong>We could not complete the search.</strong>
                    <span>Check your connection or try again in a moment.</span>
                </div>
            );
        }

        if (visibleSuggestions.length === 0) {
            return (
                <div
                    className="autocomplete-state autocomplete-empty workbench-new-autocomplete-empty"
                    role="status"
                >
                    <strong>
                        No entities found for &ldquo;{trimmedQuery}&rdquo;
                    </strong>
                    <span>
                        Try an identifier, party name, year, or another known
                        detail.
                    </span>
                </div>
            );
        }

        return visibleSuggestions.map((suggestion, index) => (
            <Button
                aria-selected={activeIndex === index}
                className={`autocomplete-option workbench-new-search-option${
                    activeIndex === index ? " active" : ""
                }`}
                htmlType="button"
                id={`workbench-new-suggestion-${index}`}
                key={suggestion.subject.id}
                onClick={(event) => handlePointerSelect(event, suggestion)}
                onMouseMove={() => setActiveIndex(index)}
                role="option"
                type="text"
            >
                <span className="workbench-new-search-option-kind">
                    <WorkbenchNewEntityTypeTag kind={suggestion.subject.kind} />
                </span>
                <span className="workbench-new-search-option-copy">
                    <span className="workbench-new-search-option-title">
                        <WorkbenchNewHighlight
                            query={query}
                            text={suggestion.title}
                        />
                    </span>
                    <small>
                        <WorkbenchNewHighlight
                            query={query}
                            text={suggestion.identifier}
                        />
                        {suggestion.party && (
                            <>
                                <span className="workbench-new-dot">.</span>
                                <WorkbenchNewHighlight
                                    query={query}
                                    text={suggestion.party}
                                />
                            </>
                        )}
                        {suggestion.period && (
                            <>
                                <span className="workbench-new-dot">.</span>
                                {suggestion.period}
                            </>
                        )}
                    </small>
                </span>
                <RightOutlined className="workbench-new-search-option-chevron" />
            </Button>
        ));
    };

    return (
        <div className="workbench-new-search">
            <div className="workbench-new-autocomplete">
                <Input
                    allowClear
                    aria-activedescendant={activeOptionId}
                    aria-autocomplete="list"
                    aria-controls="entity-suggestions"
                    aria-expanded={showPanel}
                    aria-label="Search Workbench entities"
                    onChange={handleQueryChange}
                    onFocus={() => {
                        if (showSearchInteraction) {
                            setIsOpen(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Search by ID, partner, broker, claimant or year..."
                    prefix={<SearchOutlined />}
                    role="combobox"
                    suffix={
                        <Button
                            aria-label="Open selected entity"
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={openCurrentSubject}
                        />
                    }
                    value={query}
                />
            </div>

            {showPanel && (
                <div
                    aria-label="Entity suggestions"
                    className="autocomplete-panel workbench-new-autocomplete-popup"
                    id="entity-suggestions"
                    role="listbox"
                >
                    {renderPanelContent()}
                    {renderFooter()}
                </div>
            )}
        </div>
    );
};

export default WorkbenchNewSearch;
